import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import NavBar from '../components/NavBar';
import LessonPlanDisplaySidebar from '../components/LessonPlanDisplaySidebar';
import LessonPlanPrintView from '../components/LessonPlanPrintView';
import ProtectedRoute from '../components/ProtectedRoute';
import { useFeedback } from '../contexts/FeedbackContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { savePDFToGoogleDrive } from '../utils/googleDrive';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../utils/translations';
import { track } from '../utils/analytics';
import { getMarkdownComponents, safeUrlTransform, rehypePlugins } from '../utils/markdownComponents';
import { buildLessonPlanPages } from '../utils/lessonPlanPagination';
import sharedMarkdown from '../styles/markdownShared.module.css';
import '../App.css';
import styles from './ViewLessonPlan.module.css';

// Success Modal Component
const SuccessModal = ({ isOpen, onClose, message, linkUrl, language }) => {
  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 10000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
          maxWidth: '480px',
          width: '90%',
          padding: '0',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '24px 24px 16px 24px',
          borderBottom: '1px solid #E2E3E9',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: '#34a853',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <div style={{
              width: '20px',
              height: '20px',
              position: 'relative'
            }}>
              <div style={{
                width: '3px',
                height: '10px',
                background: 'white',
                position: 'absolute',
                left: '6px',
                top: '3px',
                transform: 'rotate(45deg)'
              }} />
              <div style={{
                width: '3px',
                height: '6px',
                background: 'white',
                position: 'absolute',
                left: '10px',
                top: '6px',
                transform: 'rotate(-45deg)'
              }} />
            </div>
          </div>
          <h2 style={{
            margin: 0,
            fontSize: '1.5rem',
            fontFamily: 'Baloo 2',
            fontWeight: '600',
            color: '#0D5449',
            textTransform: 'capitalize'
          }}>
            {t('success', language) || 'Success'}
          </h2>
        </div>

        {/* Body */}
        <div style={{
          padding: '24px',
        }}>
          <p style={{
            margin: 0,
            fontSize: '1rem',
            fontFamily: 'Baloo 2',
            color: '#666',
            lineHeight: '1.5',
            marginBottom: linkUrl ? '16px' : '0'
          }}>
            {message}
          </p>
          {linkUrl && (
            <a
              href={linkUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                marginTop: '12px',
                padding: '8px 16px',
                background: '#34a853',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '6px',
                fontSize: '0.875rem',
                fontFamily: 'Azeret Mono',
                fontWeight: '600',
                textTransform: 'uppercase',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#2d8e47'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#34a853'}
            >
              {t('open_in_drive', language) || 'Open in Drive'}
            </a>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px 24px 24px',
          display: 'flex',
          justifyContent: 'flex-end',
          borderTop: '1px solid #E2E3E9'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: 'none',
              background: '#34a853',
              color: 'white',
              fontSize: '0.875rem',
              fontFamily: 'Azeret Mono',
              fontWeight: '600',
              textTransform: 'uppercase',
              cursor: 'pointer',
              transition: 'all 0.2s',
              minWidth: '100px'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#2d8e47'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#34a853'}
          >
            {t('ok', language) || 'OK'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Error Modal Component
const ErrorModal = ({ isOpen, onClose, message, language }) => {
  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 10000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
          maxWidth: '480px',
          width: '90%',
          padding: '0',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '24px 24px 16px 24px',
          borderBottom: '1px solid #E2E3E9',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: '#DC2626',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            fontSize: '24px',
            color: 'white',
            fontFamily: 'Baloo 2',
            fontWeight: '600'
          }}>
            !
          </div>
          <h2 style={{
            margin: 0,
            fontSize: '1.5rem',
            fontFamily: 'Baloo 2',
            fontWeight: '600',
            color: '#0D5449',
            textTransform: 'capitalize'
          }}>
            {t('error', language) || 'Error'}
          </h2>
        </div>

        {/* Body */}
        <div style={{
          padding: '24px',
        }}>
          <p style={{
            margin: 0,
            fontSize: '1rem',
            fontFamily: 'Baloo 2',
            color: '#666',
            lineHeight: '1.5'
          }}>
            {message}
          </p>
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px 24px 24px',
          display: 'flex',
          justifyContent: 'flex-end',
          borderTop: '1px solid #E2E3E9'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: 'none',
              background: '#DC2626',
              color: 'white',
              fontSize: '0.875rem',
              fontFamily: 'Azeret Mono',
              fontWeight: '600',
              textTransform: 'uppercase',
              cursor: 'pointer',
              transition: 'all 0.2s',
              minWidth: '100px'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#B91C1C'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#DC2626'}
          >
            {t('ok', language) || 'OK'}
          </button>
        </div>
      </div>
    </div>
  );
};

function ViewLessonPlanContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const lessonIdFromQuery = searchParams.get('id');
  const data = location.state || {};
  const { language } = useLanguage();
  const { openFeedbackPanel } = useFeedback();

  const handleFeedback = () => {
    const lessonId = lessonIdFromQuery || data.id;
    openFeedbackPanel('lesson_plan', {
      page_id: 'view-lesson-plan',
      page_name: 'View Lesson Plan',
      lesson_id: lessonId || null,
      page_elements: [
        'Lesson plan content',
        'Section navigation',
        'Edit button',
        'Print button',
        'Save to Drive button',
      ],
    });
  };
  const [grade, setGrade] = useState(data.originalSelections?.grade || '');
  const [subject, setSubject] = useState(
    data.originalSelections?.subject || data.originalSelections?.selectedSubject || data.subject || ''
  );
  const [standard, setStandard] = useState(data.originalSelections?.standard || '');
  const [concept, setConcept] = useState(data.originalSelections?.concept || '');
  const [subconcept, setSubconcept] = useState(data.originalSelections?.subconcept || '');
  const [standardDescription, setStandardDescription] = useState(data.originalSelections?.standardDescription || '');
  const [languageCode, setLanguageCode] = useState(data.originalSelections?.language || '');

  // Helper function to display language name
  const getLanguageDisplayName = (langCode) => {
    if (langCode === 'en') return 'English';
    if (langCode === 'haw' || langCode === 'hawaiian') return 'ʻŌlelo Hawaiʻi';
    return langCode; // Fallback to code if unknown
  };

  const getSubjectDisplayName = (subjectCode) => {
    if (!subjectCode) return '';
    const normalized = String(subjectCode).toLowerCase();
    if (normalized === 'cs') return 'COMPUTER SCIENCE';
    if (normalized === 'math') return 'MATH';
    if (normalized === 'science') return 'SCIENCE';
    return String(subjectCode).toUpperCase();
  };

  const inferredSubject = (() => {
    if (subject) return subject;
    const c = (concept || '').toLowerCase();
    if (c.includes('math')) return 'math';
    if (c.includes('science')) return 'science';
    if (c.includes('computer') || c.includes('cs')) return 'cs';
    return '';
  })();

  // Helper function to remove Assessment section from displayed markdown
  // Assessment is shown separately via the standalone Assessment view
  const removeAssessmentSection = (md) => {
    if (!md) return '';
    return md.replace(/##\s*Assessment\s*\n[\s\S]*?(?=\n##\s|\n$|$)/i, '');
  };

  const [selectedField, setSelectedField] = useState(null);
  const [markdown, setMarkdown] = useState(() => {
    // Normalize initial lesson plan data if provided
    if (data.lessonPlan) {
      const normalizeLessonPlan = (data) => {
        if (!data) return '';
        if (typeof data === 'string') {
          let cleaned = data.trim();
          if (cleaned.startsWith('```')) {
            cleaned = cleaned.replace(/^```(?:markdown)?\s*\n?/i, '');
            cleaned = cleaned.replace(/\n?```\s*$/i, '');
          }
          return cleaned;
        }
        return data;
      };
      return normalizeLessonPlan(data.lessonPlan);
    }
    return '';
  });
  const [loading, setLoading] = useState(false);
  const [lessonTitle, setLessonTitle] = useState(data.title || 'Untitled Lesson Plan');
  const [savingToDrive, setSavingToDrive] = useState(false);
  const [successModal, setSuccessModal] = useState({ isOpen: false, message: '', linkUrl: '' });
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: '' });

  const printRef = useRef(null);
  const lessonPlanMarkdown = useMemo(() => removeAssessmentSection(markdown), [markdown]);
  const lessonPlanPages = useMemo(() => buildLessonPlanPages(lessonPlanMarkdown), [lessonPlanMarkdown]);

  useEffect(() => {
    if (lessonIdFromQuery || data.id) track('Viewed Lesson Plan', { lesson_plan_id: lessonIdFromQuery || data.id, screen_name: 'View Lesson Plan' });
  }, [lessonIdFromQuery, data.id]);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `${lessonTitle || 'Lesson Plan'} - Kumu Connect`,
    onBeforeGetContent: () => {
      track('Clicked Print', { lesson_plan_id: lessonIdFromQuery || data.id, screen_name: 'View Lesson Plan' });
      if (!printRef.current) {
        console.error('Print component ref not found');
        return Promise.resolve();
      }
      return Promise.resolve();
    },
    onPrintError: (error) => {
      console.error('Error printing:', error);
      setErrorModal({
        isOpen: true,
        message: t('pdf_generation_error', language) || 'Failed to print. Please try again.',
      });
    },
  });

  const handleEdit = () => {
    const idToUse = lessonIdFromQuery || data.id;
    if (!idToUse) return;
    track('Clicked Edit Lesson Plan', { lesson_plan_id: idToUse, screen_name: 'View Lesson Plan' });
    navigate(`/display-lesson-plan?id=${idToUse}`, {
      state: {
        id: idToUse,
        lessonPlan: markdown,
        title: lessonTitle,
        originalSelections: {
          grade,
          subject: inferredSubject,
          standard,
          standardDescription,
          concept,
          subconcept,
          language: languageCode,
          files: data.originalSelections?.files,
          fileTypes: data.originalSelections?.fileTypes,
          selectedElements: data.originalSelections?.selectedElements,
        },
      },
    });
  };

  const handleSavePDFToDrive = async () => {
    track('Clicked Save to Drive', { lesson_plan_id: lessonIdFromQuery || data.id, screen_name: 'View Lesson Plan' });
    try {
      setSavingToDrive(true);
      
      const element = document.getElementById('lesson-plan-content');
      if (!element) throw new Error('Lesson plan content element not found');

      const html2pdf = (await import('html2pdf.js')).default;
      const opt = {
        margin: [0, 0, 0, 0],
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, letterRendering: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait', compress: true },
        pagebreak: { mode: ['css', 'legacy'], after: '.page-break-after', avoid: ['table', 'tr', 'img'] }
      };
      const pdfBlob = await html2pdf().set(opt).from(element).outputPdf('blob');

      const result = await savePDFToGoogleDrive(lessonTitle, pdfBlob);
      
      if (result.success) {
        // Open the PDF link in a new tab
        if (result.url) {
          window.open(result.url, '_blank');
        }
        setSuccessModal({
          isOpen: true,
          message: t('pdf_saved_success', language) || 'PDF saved to Google Drive successfully!',
          linkUrl: result.url || ''
        });
      }
    } catch (error) {
      console.error('Error saving PDF to Google Drive:', error);
      setErrorModal({
        isOpen: true,
        message: error.message || t('pdf_save_error', language) || 'Failed to save PDF to Google Drive. Please try again.'
      });
    } finally {
      setSavingToDrive(false);
    }
  };

  // Function to normalize lesson plan data (handles both markdown and legacy JSON formats)
  const normalizeLessonPlan = (data) => {
    // Handle null/undefined data
    if (!data) {
      return 'No lesson plan data available';
    }

    // If it's already a string (markdown), return it directly
    if (typeof data === 'string') {
      // Remove any markdown code fences if present
      let cleaned = data.trim();
      if (cleaned.startsWith('```')) {
        cleaned = cleaned.replace(/^```(?:markdown)?\s*\n?/i, '');
        cleaned = cleaned.replace(/\n?```\s*$/i, '');
      }
      return cleaned;
    }
    
    // Legacy JSON format support (for backward compatibility)
    if (typeof data === 'object' && data !== null) {
      const markdownParts = [];
      
      // Add title
      if (data["Lesson Plan Title"]) {
        markdownParts.push(`# ${data["Lesson Plan Title"]}\n`);
      }
      
      // Add lesson overview
      if (data["Lesson Overview"]) {
        markdownParts.push(`## Lesson Overview\n${data["Lesson Overview"]}\n`);
      }
      
      // Add learning objectives
      if (data["Learning Objectives"]) {
        markdownParts.push("## Learning Objectives");
        if (Array.isArray(data["Learning Objectives"])) {
          data["Learning Objectives"].forEach(obj => {
            markdownParts.push(`- ${obj}`);
          });
        } else {
          markdownParts.push(data["Learning Objectives"]);
        }
        markdownParts.push("");
      }
      
      // Add Hawaiian Cultural Connection
      if (data["Hawaiian Cultural Connection"]) {
        markdownParts.push(`## Hawaiian Cultural Connection\n${data["Hawaiian Cultural Connection"]}\n`);
      }
      
      // Add lesson procedure
      if (data["Lesson Procedure"]) {
        markdownParts.push("## Lesson Procedure");
        const procedure = data["Lesson Procedure"];
        
        if (typeof procedure === 'object' && procedure !== null) {
          Object.keys(procedure).forEach(key => {
            if (key === "Introduction" && Array.isArray(procedure[key])) {
              markdownParts.push(`### ${key}`);
              procedure[key].forEach(item => {
                markdownParts.push(`- ${item}`);
              });
              markdownParts.push("");
            } else if (key === "Main Activity" && typeof procedure[key] === 'object') {
              const mainActivity = procedure[key];
              markdownParts.push(`### ${mainActivity.title || key}`);
              if (mainActivity.phases && Array.isArray(mainActivity.phases)) {
                mainActivity.phases.forEach(phase => {
                  if (phase.phase_title) {
                    markdownParts.push(`#### ${phase.phase_title}`);
                  }
                  if (phase.description) {
                    markdownParts.push(phase.description);
                  }
                  markdownParts.push("");
                });
              }
            } else if (key === "Reflection & Discussion" && Array.isArray(procedure[key])) {
              markdownParts.push(`### ${key}`);
              procedure[key].forEach(item => {
                markdownParts.push(`- ${item}`);
              });
              markdownParts.push("");
            }
          });
        }
        markdownParts.push("");
      }
      
      // Assessment generation is disabled for now.
      // Skip rendering legacy Assessment content in normalized markdown.
      
      // Add worksheet (support both new and legacy key variants)
      const worksheetKey = Object.keys(data).find(
        key => key?.trim().toLowerCase().startsWith("worksheet")
      );
      const worksheetData = data["Worksheet"] || (worksheetKey ? data[worksheetKey] : null);
      if (worksheetData) {
        markdownParts.push("## Worksheet");
        const worksheet = worksheetData;
        if (typeof worksheet === 'object' && worksheet !== null) {
          if (worksheet.title) {
            markdownParts.push(`### ${worksheet.title}`);
          }
          if (worksheet.instructions) {
            markdownParts.push(worksheet.instructions);
          }
          if (worksheet.sections && Array.isArray(worksheet.sections)) {
            worksheet.sections.forEach(section => {
              if (section.header) {
                markdownParts.push(`#### ${section.header}`);
              }
              if (section.content && Array.isArray(section.content)) {
                section.content.forEach(item => {
                  markdownParts.push(item);
                });
              }
              markdownParts.push("");
            });
          }
        }
        markdownParts.push("");
      }
      
      // Add reflection questions
      if (data["Reflection Questions"]) {
        markdownParts.push("## Reflection Questions");
        if (Array.isArray(data["Reflection Questions"])) {
          data["Reflection Questions"].forEach((question, index) => {
            markdownParts.push(`${index + 1}. ${question}`);
          });
        } else {
          markdownParts.push(data["Reflection Questions"]);
        }
        markdownParts.push("");
      }
      
      // Add extensions
      if (data["Extensions"]) {
        markdownParts.push("## Extensions");
        if (Array.isArray(data["Extensions"])) {
          data["Extensions"].forEach(item => {
            markdownParts.push(`- ${item}`);
          });
        } else {
          markdownParts.push(data["Extensions"]);
        }
        markdownParts.push("");
      }
      
      // Add conclusion
      if (data["Conclusion"]) {
        markdownParts.push(`## Conclusion\n${data["Conclusion"]}\n`);
      }
      
      return markdownParts.join("\n");
    }
    
    // Fallback: return data as-is
    return data;
  };

  // Fetch lesson plan data if we have an ID (prefer query param)
  useEffect(() => {
    const idToFetch = lessonIdFromQuery || data.id;
    if (idToFetch && !data.lessonPlan) {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      fetch(`/api/lesson-plan/${idToFetch}/`, {
        headers: {
          ...(token && { 'Authorization': `Token ${token}` }),
        },
      })
        .then(response => {
          if (!response.ok) {
            return response.json().then(err => Promise.reject(err)).catch(() => Promise.reject(new Error('Failed to load lesson plan')));
          }
          return response.json();
        })
        .then(lessonData => {
          if (lessonData.error) {
            setMarkdown('');
            setLoading(false);
            return;
          }
          const rawContent = lessonData.lesson_plan ?? lessonData.lessonPlan;
          const normalizedMarkdown = normalizeLessonPlan(rawContent);
          setMarkdown(normalizedMarkdown);
          if (lessonData.title) setLessonTitle(lessonData.title);
          if (lessonData.grade) setGrade(lessonData.grade);
          if (lessonData.standard) setStandard(lessonData.standard);
          if (lessonData.subject) setSubject(lessonData.subject);
          if (lessonData.concept) setConcept(lessonData.concept);
          if (lessonData.subconcept) setSubconcept(lessonData.subconcept);
          if (lessonData.standard_description || lessonData.standardDescription) {
            setStandardDescription(lessonData.standard_description || lessonData.standardDescription);
          }
          if (lessonData.language) setLanguageCode(lessonData.language);
          setLoading(false);
        })
        .catch(error => {
          console.error('Error fetching lesson plan:', error);
          setMarkdown('');
          setLoading(false);
          setErrorModal({
            isOpen: true,
            message: error?.message || t('load_lesson_plan_error', language) || 'Could not load this lesson plan. Try opening it again from My Plans.'
          });
        });
    }
  }, [lessonIdFromQuery, data.id, data.lessonPlan]);

  if (loading) {
    return (
      <div className={styles.container}>
        <NavBar />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <div>{t('loading_lesson_plan', language)}</div>
        </div>
      </div>
    );
  }

  const renderLessonMetaHeader = (showEdit = false) => (
    <div className={styles.lessonMetaHeader}>
      <div className={styles.lessonMetaTopRow}>
        <div className={styles.lessonMetaTags}>
          {grade && <span className={styles.metaTag}>{grade}</span>}
          {languageCode && <span className={styles.metaTag}>{getLanguageDisplayName(languageCode).toUpperCase()}</span>}
          {inferredSubject && <span className={styles.metaTag}>{getSubjectDisplayName(inferredSubject)}</span>}
        </div>
        {showEdit && (
          <button type="button" className={styles.editButton} onClick={handleEdit}>
            {t('edit', language) || 'Edit'}
          </button>
        )}
      </div>
      <div className={styles.lessonMetaBody}>
        <img
          src="/assets/KumuConnectLogos/Logo_GreenonLight.png"
          alt="Kumu Connect"
          className={styles.lessonMetaLogo}
        />
        <div className={styles.lessonMetaText}>
          <h1 className={styles.lessonTitleCompact}>{lessonTitle?.trim() || `${grade || 'Grade'} ${standard || 'Lesson Plan'}`}</h1>
          <p className={styles.lessonConceptCompact}>{concept || getSubjectDisplayName(inferredSubject) || 'Lesson'}</p>
          <p className={styles.lessonStandardCompact}>
            {standardDescription ? `${standard || 'N/A'}: ${standardDescription}` : (standard || 'N/A')}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className={styles.container}>
      <SuccessModal
        isOpen={successModal.isOpen}
        onClose={() => setSuccessModal({ isOpen: false, message: '', linkUrl: '' })}
        message={successModal.message}
        linkUrl={successModal.linkUrl}
        language={language}
      />
      <ErrorModal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ isOpen: false, message: '' })}
        message={errorModal.message}
        language={language}
      />
      <NavBar />
      <div className={styles.mainLayout}>
        <div className={styles.sidebar}>
          <LessonPlanDisplaySidebar 
            data={markdown} 
            onFieldClick={setSelectedField} 
            selectedField={selectedField}
            currentView="full"
            lessonData={{
              id: lessonIdFromQuery || data.id,
              title: lessonTitle,
              lessonPlan: markdown,
              originalSelections: {
                grade,
                subject,
                standard,
                standardDescription,
                concept,
                language: languageCode,
                files: data.originalSelections?.files,
                fileTypes: data.originalSelections?.fileTypes,
                selectedElements: data.originalSelections?.selectedElements,
              }
            }}
          />
        </div>
        <div className={styles.editorWrapper}>
          <div id="lesson-plan-content">
            <div className={styles.a4PagesStack}>
              {lessonPlanPages.map((page, index) => (
                <React.Fragment key={page.key}>
                  {index > 0 && <div className={styles.pageBreakIndicator}>Page Break</div>}
                  <section
                    className={`${styles.a4Page} ${index < lessonPlanPages.length - 1 ? `page-break-after ${styles.a4PageWithBreak}` : ''}`}
                  >
                    {page.includeHeader && renderLessonMetaHeader(index === 0)}
                    <div className={styles.markdownContent}>
                      {(page.sections || []).map((section, sectionIndex) => (
                        <React.Fragment key={section.key || `${page.key}-section-${sectionIndex}`}>
                          <section className={styles.sectionBlock}>
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              rehypePlugins={rehypePlugins}
                              urlTransform={safeUrlTransform}
                              components={getMarkdownComponents({ ...styles, ...sharedMarkdown }, { handleDrawingSpace: true, hideDrawingSpace: true })}
                            >
                              {section.markdown}
                            </ReactMarkdown>
                          </section>
                          {sectionIndex < (page.sections || []).length - 1 && (
                            <div className={styles.sectionDivider} aria-hidden="true">
                              <span className={styles.sectionDividerLine} />
                              <img
                                src="/assets/leaf_logo_white_bg.svg"
                                alt=""
                                className={styles.sectionDividerIcon}
                              />
                              <span className={styles.sectionDividerLine} />
                            </div>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </section>
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Print-only component (hidden visually, accessible to react-to-print) */}
          <LessonPlanPrintView
            ref={printRef}
            lessonTitle={lessonTitle}
            grade={grade}
            subject={inferredSubject}
            concept={concept}
            subconcept={subconcept}
            standard={standard}
            standardDescription={standardDescription}
            languageLabel={getLanguageDisplayName(languageCode)}
            markdown={lessonPlanMarkdown}
            includeAssessment={false}
          />
        </div>
      </div>
      <div className={styles.feedbackButtonContainer}>
        <button
          type="button"
          className={styles.feedbackBtn}
          onClick={handleFeedback}
          aria-label={t('feedback', language)}
        >
          <img src="/assets/light_bulb.svg" alt="" className={styles.feedbackBtnIcon} />
          <span>{t('feedback', language)}</span>
        </button>
      </div>
      <div className={styles.actionButtons}>
        <button 
          className={styles.googleDriveButton}
          onClick={handleSavePDFToDrive}
          disabled={savingToDrive}
        >
          {savingToDrive ? t('saving', language) : t('save_pdf_to_drive', language)}
        </button>
        <button 
          className={styles.printButton}
          onClick={handlePrint}
        >
          {t('print', language)}
        </button>
      </div>
    </div>
  );
}

function ViewLessonPlan() {
  return (
    <ProtectedRoute>
      <ViewLessonPlanContent />
    </ProtectedRoute>
  );
}

export default ViewLessonPlan;
